import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import staffModel, {
  Availability,
  Calendso,
  CalendsoEventType,
  ContactDto,
  CreateStaffDto,
  Staff,
  StaffAvailability,
  StaffState,
  UpdateStaffDto,
} from '@/core/models/staff.model'
import { SearchRequestOptions, SearchStaffsAvailabilityRequestParams } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'

// TODO: extract calendso to its own reducer
export type StaffReducerState = StaffState & {
  calendsoByStaff: Record<string, Calendso>
  createDirectMsg: Record<string, any>
  archiveRoom: Record<string, any>
}

export const staffAdapter = createEntityAdapter({
  selectId: (staff: Staff) => {
    if (staff._id) {
      return staff._id
    } else {
      staff._id = staff.pluginId
      return staff._id
    }
  },
})

const staffSelector = staffAdapter.getSelectors((state: RootState) => state.staff.staffs)
export const selectStaffs = staffSelector.selectAll
export const selectStaffById = staffSelector.selectById

export const selectCalendsoByStaffId = (state: RootState, staffId: string) => state.staff.calendsoByStaff[staffId]
export const selectStaffGetRequestTracker = (state: RootState) => state.staff.requestTracker.get
export const selectStaffSignUpCalendsoRequestTracker = (state: RootState) => state.staff.requestTracker.signUpCalendso

export const selectNewlyCreatedStaffId = (state: RootState) => state.staff.newlyCreatedItemId
export const selectStaffCreateRequestTracker = (state: RootState) => state.staff.requestTracker.create

const staffCalendsoEventTypeAdapter = createEntityAdapter({
  selectId: (calendsoEventType: { id: string; events: CalendsoEventType[] }) => calendsoEventType.id,
})

const staffCalendsoEventTypeSelector = staffCalendsoEventTypeAdapter.getSelectors((state: RootState) => state.staff.calendsoEventTypesByStaff)

const staffAvailabilityAdapter = createEntityAdapter({
  selectId: (staffAvailability: StaffAvailability) => staffAvailability.id,
})

const staffAvailabilitySelector = staffAvailabilityAdapter.getSelectors((state: RootState) => state.staff.availabilitiesByStaff)

export const selectStaffCalendsoEventTypes = staffCalendsoEventTypeSelector.selectAll
export const selectStaffAvailabilities = staffAvailabilitySelector.selectAll
export const selectCalendsoEventTypeByStaffId = staffCalendsoEventTypeSelector.selectById
export const selectAvailabilityByStaffId = staffAvailabilitySelector.selectById
export const selectStaffSearchParams = (state: RootState) => state.staff.search.params
export const selectStaffSearchResult = (state: RootState) => state.staff.search.result
export const selectStaffListRequestTracker = (state: RootState) => state.staff.requestTracker.list
export const selectConfigureStripePaymentRequestTracker = (state: RootState) => state.staff.requestTracker.configureStripePayment
export const selectDeleteStripePaymentRequestTracker = (state: RootState) => state.staff.requestTracker.deleteStripeAccount

const initialState: StaffReducerState = {
  ...staffModel.getInitialState(),
  calendsoByStaff: {},
  requestTracker: {
    ...staffModel.getInitialState().requestTracker,
    getCalendso: { error: null, loading: false },
    signUpCalendso: { error: null, loading: false },
    configureStripePayment: { error: null, loading: false },
    deleteStripeAccount: { error: null, loading: false },
  },
  createDirectMsg: { error: null, loading: false, room: {} },
  archiveRoom: { error: null, loading: false, discussion: 0 },
}

const staffSlice = createSlice({
  name: staffModel.reducerName,
  initialState,
  reducers: {
    getStaff(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getStaffSucess(state, action: PayloadAction<Staff>) {
      staffAdapter.upsertOne(state.staffs, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getStaffFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    getStaffs: {
      reducer: (state, action: PayloadAction<{ query: Record<string, any>; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
      },
      prepare: (query: Record<string, any>, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    getStaffsSuccess(state, action: PayloadAction<{ items: Staff[]; totalCount: number }>) {
      staffAdapter.setAll(state.staffs, action.payload.items)
      state.search.result.totalCount = action.payload.totalCount
      state.newlyCreatedItemId = undefined
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getStaffsFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },

    // #region calcom integration

    getStaffAvailability: {
      reducer: (
        state,
        action: PayloadAction<{
          username: string
          dateFrom: string
          dateTo: string
          staffId: string
        }>
      ) => {
        // staffModel.getStaffAvailability(calendso, duration, offset)
        state.requestTracker.list.loading = true
      },
      prepare: (username: string, dateFrom: string, dateTo: string, staffId: string) => ({
        payload: {
          username,
          dateFrom,
          dateTo,
          staffId,
        },
      }),
    },
    // getStaffAvailabilitySuccess(state, action: PayloadAction<{ staffId: string, slots: Slot[] }>) {
    getStaffAvailabilitySuccess(state, action: PayloadAction<{ staffId: string; availability: Availability }>) {
      const { availability, staffId } = action.payload
      staffAvailabilityAdapter.upsertOne(state.availabilitiesByStaff, { id: staffId, availability })
    },
    getStaffAvailabilityFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },

    getStaffsAvailability(state, action: PayloadAction<SearchStaffsAvailabilityRequestParams>) {
      // staffModel.getStaffAvailability(calendso, duration, offset)
      state.requestTracker.list.loading = true
    },
    getStaffsAvailabilitySuccess(state, action: PayloadAction<{ id: string; availability: Availability }[]>) {
      staffAvailabilityAdapter.setAll(state.availabilitiesByStaff, action.payload)
      state.requestTracker.list.loading = false
    },
    getStaffsAvailabilityFailure(state) {
      state.requestTracker.list.loading = false
    },

    getStaffsEvents(state, action: PayloadAction<{ staffId: string }[]>) {
      state.requestTracker.list.loading = true
    },
    getStaffsEventsSuccess(state, action: PayloadAction<{ events: CalendsoEventType[]; id: string }[]>) {
      staffCalendsoEventTypeAdapter.setAll(state.calendsoEventTypesByStaff, action.payload)
      state.requestTracker.list.loading = false
    },
    getStaffsEventsFailure(state) {
      state.requestTracker.list.loading = false
    },

    // #endregion

    handleContact(state, action: PayloadAction<ContactDto>) {
      state.requestTracker.create.loading = true
    },
    handleContactSuccess(state, action) {
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    handleContactFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    createDirectMsg: {
      reducer: (state, action: PayloadAction<{ email: string }>) => {
        state.createDirectMsg.loading = true
      },
      prepare: (email: string) => ({
        payload: {
          email,
        },
      }),
    },
    createDirectMsgSuccess(state, action: PayloadAction<{ room: any }>) {
      state.createDirectMsg.room = action.payload
      state.createDirectMsg.error = null
      state.createDirectMsg.loading = false
    },
    createDirectMsgFailure(state, action) {
      state.createDirectMsg.error = action.payload
      state.createDirectMsg.loading = false
    },
    archiveRoom: {
      reducer: (state, action: PayloadAction<{ roomId: string }>) => {
        state.archiveRoom.loading = true
      },
      prepare: (roomId: string) => ({
        payload: {
          roomId,
        },
      }),
    },
    archiveRoomSuccess(state, action: PayloadAction<{ discussion: any }>) {
      state.archiveRoom.discussion = action.payload
      state.archiveRoom.error = null
      state.archiveRoom.loading = false
    },
    archiveRoomFailure(state, action) {
      state.archiveRoom.error = action.payload
      state.archiveRoom.loading = false
    },
    addStaff: {
      reducer: (state, action: PayloadAction<CreateStaffDto>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (staff: CreateStaffDto) => ({
        payload: {
          ...staff,
        },
      }),
    },
    addStaffSuccess(state, action: PayloadAction<Staff>) {
      staffAdapter.addOne(state.staffs, action.payload)
      state.newlyCreatedItemId = action.payload._id
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    addStaffFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    updateStaff: {
      reducer(state, action: PayloadAction<{ staffId: string; changes: UpdateStaffDto }>) {
        state.requestTracker.update.loading = true
      },
      prepare: (staffId: string, changes: UpdateStaffDto) => ({
        payload: { staffId, changes },
      }),
    },
    updateStaffSuccess(state, action: PayloadAction<Staff>) {
      staffAdapter.upsertOne(state.staffs, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateStaffFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    configureStripePayment(state, action: PayloadAction<{ staffId: string; refreshUrl: string; returnUrl: string }>) {
      state.requestTracker.configureStripePayment.loading = true
    },
    configureStripePaymentSuccess(state) {
      state.requestTracker.configureStripePayment.error = null
      state.requestTracker.configureStripePayment.loading = false
    },
    configureStripePaymentFailure(state, action) {
      state.requestTracker.configureStripePayment.error = action.payload
      state.requestTracker.configureStripePayment.loading = false
    },
    deleteStripeAccount(state, action: PayloadAction<{ staffId: string }>) {
      state.requestTracker.deleteStripeAccount.loading = true
    },
    deleteStripeAccountSuccess(state) {
      state.requestTracker.deleteStripeAccount.error = null
      state.requestTracker.deleteStripeAccount.loading = false
    },
    deleteStripeAccountFailure(state, action) {
      state.requestTracker.deleteStripeAccount.error = action.payload
      state.requestTracker.deleteStripeAccount.loading = false
    },

  },
})

export const {
  getStaff,
  getStaffSucess,
  getStaffFailure,
  getStaffs,
  getStaffsSuccess,
  getStaffsFailure,
  getStaffAvailability,
  getStaffAvailabilitySuccess,
  getStaffAvailabilityFailure,
  getStaffsAvailability,
  getStaffsAvailabilitySuccess,
  getStaffsAvailabilityFailure,
  getStaffsEvents,
  getStaffsEventsSuccess,
  getStaffsEventsFailure,
  handleContact,
  handleContactSuccess,
  handleContactFailure,
  createDirectMsg,
  createDirectMsgSuccess,
  createDirectMsgFailure,
  archiveRoom,
  archiveRoomSuccess,
  archiveRoomFailure,
  addStaff,
  addStaffSuccess,
  addStaffFailure,
  updateStaff,
  updateStaffSuccess,
  updateStaffFailure,
  configureStripePayment,
  configureStripePaymentSuccess,
  configureStripePaymentFailure,
  deleteStripeAccount,
  deleteStripeAccountSuccess,
  deleteStripeAccountFailure,
} = staffSlice.actions

export default staffSlice.reducer
